import axiosInstance from '../config/axios';

export interface Permission {
  id: string;
  name: string;
  description: string;
}

export interface AdminPermission {
  id: string;
  name: string;
}

export interface StagePermissionResponse {
  permissions: string[];
  isIncharge: boolean;
}

class PermissionService {
  private permissionCache: Map<string, string> = new Map();
  private uuidToNameCache: Map<string, string> = new Map();
  private adminPermissionCache: Map<string, string> = new Map();
  private cacheInitialized = false;

  async initializeCache(): Promise<void> {
    if (this.cacheInitialized) return;

    try {
      const response = await axiosInstance.post('/api/organizations/permissions');
      const permissions: Permission[] = response.data;

      const adminresponse = await axiosInstance.post('/api/superadmin/permissions');
      const adminPermissions: AdminPermission[] = adminresponse.data;

      this.permissionCache.clear();
      this.uuidToNameCache.clear();
      this.adminPermissionCache.clear();

      adminPermissions.forEach(permission => {
        this.adminPermissionCache.set(permission.name, permission.id);
      });

      permissions.forEach(permission => {
        this.permissionCache.set(permission.name, permission.id);
        this.uuidToNameCache.set(permission.id, permission.name);
      });

      this.cacheInitialized = true;
    } catch (error) {
      console.error('Failed to initialize permission cache:', error);
    }
  }

  getPermissionId(permissionName: string): string | undefined {
    return this.permissionCache.get(permissionName);
  }

  getPermissionName(permissionId: string): string | undefined {
    return this.uuidToNameCache.get(permissionId);
  }

  getAdminPermissionId(permissionName: string): string | undefined {
    return this.adminPermissionCache.get(permissionName);
  }

  getAdminPermissionName(permissionId: string): string | undefined {
    for (const [name, id] of Array.from(this.adminPermissionCache.entries())) {
      if (id === permissionId) return name;
    }
    return undefined;
  }

  hasPermissionByName(userPermissionIds: string[], permissionName: string): boolean {
    const permissionId = this.getPermissionId(permissionName);
    if (!permissionId) return false;
    return userPermissionIds.includes(permissionId);
  }

  hasAdminPermissionByName(userPermissionIds: string[], permissionName: string): boolean {
    const permissionId = this.getAdminPermissionId(permissionName);
    if (!permissionId) return false;
    return userPermissionIds.includes(permissionId);
  }

  hasPermissionById(userPermissionIds: string[], permissionId: string): boolean {
    return userPermissionIds.includes(permissionId);
  }

  getAllPermissions(): { id: string; name: string }[] {
    return Array.from(this.uuidToNameCache.entries()).map(([id, name]) => ({ id, name }));
  }

  clearCache(): void {
    this.permissionCache.clear();
    this.uuidToNameCache.clear();
    this.cacheInitialized = false;
  }

  /**
   * Get permissions for a specific stage, party, and user
   */
  async getStagePermissions(stageId: string, userId: string, partyId: string): Promise<StagePermissionResponse> {
    try {
      const response = await axiosInstance.post('/api/permissions/getpermissionsbystage', {
        stageId,
        userId,
        partyId
      });

      return {
        permissions: response.data?.permissions || [],
        isIncharge: response.data?.isIncharge || false
      };
    } catch (error) {
      console.error('Failed to fetch stage permissions:', error);
      return { permissions: [], isIncharge: false };
    }
  }

  /**
   * Refresh stage permissions for current user and party
   * This method fetches fresh permissions and updates localStorage
   */
  async refreshStagePermissions(stageId: string, userId: string, partyId: string): Promise<string[]> {
    try {
      const response = await this.getStagePermissions(stageId, userId, partyId);

      localStorage.setItem('stagePermissions', JSON.stringify(response.permissions));
      localStorage.setItem('stagePermissionsPartyId', partyId);
      
      const existingMeta = localStorage.getItem('selectedStageMeta');
      if (existingMeta) {
        try {
          const meta = JSON.parse(existingMeta);
          meta.isIncharge = response.isIncharge;
          const updatedMeta = JSON.stringify(meta);
          localStorage.setItem('selectedStageMeta', updatedMeta);
          
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'selectedStageMeta',
            newValue: updatedMeta
          }));
        } catch (e) {
          console.error('Failed to update stage meta with isIncharge:', e);
        }
      }

      window.dispatchEvent(new StorageEvent('storage', {
        key: 'stagePermissions',
        newValue: JSON.stringify(response.permissions)
      }));

      window.dispatchEvent(new StorageEvent('storage', {
        key: 'stagePermissionsPartyId',
        newValue: partyId
      }));

      return response.permissions;
    } catch (error) {
      console.error('Failed to refresh stage permissions:', error);
      localStorage.removeItem('stagePermissions');
      localStorage.removeItem('stagePermissionsPartyId');
      return [];
    }
  }
}

export const permissionService = new PermissionService();